import { sportslineGraphQLClient } from '@lib/graphql-client';
import { PickSnippetInput, SportslineWebComponent_PickSnippetQuery } from '@lib/graphql-types';
import { Component, Host, Prop, State, h, Element } from '@stencil/core';
import { isGameAvailableForBetting } from '@utils/bettingUtils';
import { getBookAssetsById, getBookSloganById } from '@utils/books';
import { generateRandomAlphaNumericId, isUndefinedOrNull } from '@utils/commonUtils';
import { observeVisibilityAndTrigger } from '@utils/html';
import { fromPickSnippetAttributeToTrackingImpressionWidgetDataBets, fromPickSnippetAttributeToTrackingViewedWidgetDataBets, toPickSnippetInput } from '@utils/pickSnippet';
import { fromPickSnippetToPickSnippetCardProps, getDataFromAttribute } from '@utils/sidelineUtils';
import { eventsName, PickSnippetEventViewed, setInstanceIdAttribute, trackEvent } from '@utils/trackingUtils';

export type PickSnippetArgsData = {
  line?: number | null;
  selectionId: string;
  teamAbbr?: null | string;
  teamId?: number | null;
  vendorGameId: string;
  vendorMarketId: string;
  gameId: string;
  marketId: number;
  marketName: string;
  marketType: 'team' | 'player' | 'game' | 'standard';
  odds: number;
  pick: 'over' | 'under' | 'yes' | 'no' | 'home' | 'away' | 'draw' | 'standard' | 'unknown';
  playerId?: string;
  playerName?: string;
  type: 'total' | 'spread' | 'moneyline' | 'overUnder' | 'yesNo';
  vendorPlayerId?: string;
  vendorTeamId?: string;
};

export type SelectionIds = {
  gameId: string;
  playerId: string | null;
  teamId: string | null;
  vendorGameId: string;
  vendorPlayerId?: string;
  vendorTeamId?: string;
};

@Component({
  tag: 'sportsline-pick-snippet',
  styleUrl: 'sportsline-pick-snippet.css',
  shadow: true,
})
export class SportslinePickSnippet {
  /**
   * Input for the pick snippet.
   * This should contain the necessary fields to fetch the pick data.
   */
  @Prop() pick: PickSnippetInput;
  /**
   * Whether the snippet is branded
   */
  @Prop() isBranded: boolean = false;
  /**
   * Book ID, e.g., "35"
   */
  @Prop() book: string | null = null;
  /**
   * Book Name, e.g., "FanDuel"
   */
  @Prop() bookName: string | null = null;
  /**
   * Vendor Book ID, e.g., "value"
   */
  @Prop() vendorBookId: string | null = null;
  @State() data: SportslineWebComponent_PickSnippetQuery['pickSnippet'] = null;
  @State() loading: boolean = true;
  @State() error: string | null = null;
  @State() initialized: boolean = false;

  @Element() el: HTMLSportslinePickSnippetElement;

  // create a unique instance id for tracking
  private instanceId: string = generateRandomAlphaNumericId();
  private version: string = '1.0.0';
  private get name() {
    // Updating name based on branding
    if (this.getIsBrandedDataValid) {
      return 'branded-pick-snippet';
    }
    return 'pick-snippet';
  }

  private client = sportslineGraphQLClient();

  private get getIsBrandedDataValid() {
    try {
      return [this.isBranded, Boolean(this.book), Boolean(this.bookName), Boolean(this.vendorBookId)].every(Boolean);
    } catch (error) {
      console.error('Error validating branded data:', error);
      return false;
    }
  }

  private get getBookName() {
    try {
      if (this.book === null || this.book === '' || typeof this.book !== 'string' || !getBookAssetsById(this.book)) {
        return null;
      }
      return this.bookName || getBookAssetsById(this.book).name || 'Sportsbook';
    } catch (error) {
      console.error('Error getting book name:', error);
      return null;
    }
  }

  private get getWidgetDataInfo() {
    return {
      name: this.name,
      instanceId: this.instanceId,
      version: this.version,
      ...(this.getIsBrandedDataValid && {
        promotion: {
          operator: this.getBookName ? this.getBookName.toLowerCase() : 'sportsbook',
        },
      }),
      ref: this.el,
    };
  }

  private trackError(error: Error) {
    this.error = `Error fetching data: ${error.message}`;
    console.warn('Error fetching data:', error);
    trackEvent({
      action: 'error',
      eventName: eventsName.martech,
      payload: {
        error: this.error,
      },
      widgetData: this.getWidgetDataInfo,
    });
  }

  private async fetch(input: PickSnippetInput) {
    this.initialized = true; // Mark as initialized to allow rendering
    this.loading = true;
    this.error = null;

    // fetching data, before loading the component
    if (!Boolean(input)) {
      console.warn('No input provided for SportslinePickSnippet');
      this.loading = false;
      this.error = 'No input provided for SportslinePickSnippet';
      return Promise.resolve();
    }

    return this.client.dataFetcher
      .SportslineWebComponent_PickSnippet({
        input,
      })
      .then(response => {
        const data = response.data;
        if (Boolean(response.errors?.length)) {
          throw new Error(`GraphQL errors: ${response.errors.map(error => error.message).join(', ')}`);
        }
        if (!Boolean(data?.pickSnippet)) {
          throw new Error('No pick snippet data found');
        }
        this.data = data.pickSnippet;
        const dataAttr = getDataFromAttribute(this.getDataPickAttribute);
        const event: PickSnippetEventViewed = {
          action: 'viewed',
          eventName: eventsName.martech,
          widgetData: {
            ...this.getWidgetDataInfo,
            bets: [
              fromPickSnippetAttributeToTrackingViewedWidgetDataBets({
                dataAttr,
                pick: data.pickSnippet,
              }),
            ],
          },
        };
        trackEvent(event);
      });
  }

  async componentWillLoad() {
    // check if we have data-instance-id attribute, if so, use it for the instanceId,
    // if not generate one and set it as attribute
    this.instanceId = setInstanceIdAttribute(this.el, this.instanceId);

    try {
      const input = this.getInput;
      if (!Boolean(input)) {
        throw new Error('Invalid input provided for SportslinePickSnippet');
      }
      // Track the initial impression event
      trackEvent({
        action: 'impression',
        eventName: eventsName.martech,
        widgetData: {
          ...this.getWidgetDataInfo,
          bets: [
            fromPickSnippetAttributeToTrackingImpressionWidgetDataBets({
              dataAttr: getDataFromAttribute(this.getDataPickAttribute),
            }),
          ],
        },
      });
      // Fetch data when the component is about to be visible
      observeVisibilityAndTrigger({
        element: this.el,
        callback: async () => {
          await this.fetch(input)
            .catch((error: Error) => {
              // Track error if fetching fails
              this.trackError(error);
            })
            .finally(() => {
              this.loading = false;
              this.initialized = true; // Mark as initialized after fetching
            });
        },
      });
    } catch (error) {
      this.trackError(error);
      this.loading = false;
      this.initialized = true; // Mark as initialized after fetching
    }
  }

  private get renderVendorButton() {
    const className = 'cbs-widget';
    const alreadyRendered = this.el.getElementsByClassName(className).length > 0;
    if (alreadyRendered) {
      // Prevent duplicate widget rendering
      return undefined;
    }
    const gameUnavailableForBetting = !isGameAvailableForBetting(this.data?.game);
    if (gameUnavailableForBetting) {
      // Do not render if the game is not available for betting
      return undefined;
    }
    const missingSideLinesData = !Boolean(this.getData?.sideLinesData);
    if (missingSideLinesData) {
      // Do not render if sideLinesData is missing
      return undefined;
    }
    const el = document.createElement('div');
    el.classList.add(className);

    el.setAttribute(
      'data-config',
      JSON.stringify({
        ...this.getData.sideLinesData,
        instanceId: this.instanceId,
        ...(this.getIsBrandedDataValid && {
          operatorName: this.getBookName,
          meta: {
            promotion: {
              operator: this.getBookName
            }
         },
          type: 'branded-pick-snippet',
          buttonText: `Bet at ${this.getBookName}`,
        }),
      }),
    );
    this.el.appendChild(el);
  }

  private get renderVendorDisclaimer() {
    const className = 'cbs-widget-disclaimer';
    const validations = [this.el.getElementsByClassName(className).length > 0];
    if (validations.some(Boolean)) {
      return;
    }
    const el = document.createElement('div');
    el.setAttribute('slot', 'disclaimer');
    el.innerHTML = `Betting odds are subject to change. Please gamble responsibly.`;
    el.classList.add(className);
    this.el.appendChild(el);
  }

  private get getVendorData() {
    const validations = [!Boolean(this.data)];
    if (validations.some(Boolean)) {
      return null;
    }

    return JSON.stringify(this.data ?? null);
  }

  private get getDataPickAttribute() {
    return this.el.getAttribute('data-pick') ?? null;
  }

  // Remove typing and place correct one
  private get getDataFromAttribute() {
    return getDataFromAttribute(this.getDataPickAttribute);
  }

  private get getInput() {
    const dataPick = this.getDataFromAttribute;
    if (dataPick !== null) {
      try {
        return toPickSnippetInput(dataPick);
      } catch (error) {
        console.warn('Invalid input format for SportslinePickSnippet:', error);
        return null;
      }
    }
    return this.pick;
  }

  private get getData() {
    return fromPickSnippetToPickSnippetCardProps({
      data: this.data,
      dataAttr: this.getDataPickAttribute,
    });
  }

  render() {
    if (!this.initialized) return null; // Prevent rendering before initialization

    if (this.loading) {
      return (
        <Host>
          <div class="sportsline-pick-snippet--loading">
            <sportsline-circular-progress size="sm" />
          </div>
        </Host>
      );
    }
    if (Boolean(this.error)) {
      return null; // or you can render an error message if needed
      // return <Host>{this.instanceId}</Host>; // or you can render an error message if needed
    }
    const data = this.getData;

    if (isUndefinedOrNull(data)) return null;

    const isBranded = this.getIsBrandedDataValid;

    const { gameInfoLabel, type, teamId, teamName, playerId, playerName, activeColor, label, marketLabel, odds, side } = this.getData;
    return (
      <Host>
        <sportsline-pick-snippet-card
          isBranded={isBranded}
          bookId={this.book}
          bookName={this.getBookName}
          bookSlogan={getBookSloganById(this.book)}
          gameInfoLabel={gameInfoLabel}
          type={type}
          playerId={playerId}
          teamId={teamId}
          teamName={teamName}
          playerName={playerName}
          activeColor={activeColor}
          label={label}
          marketLabel={marketLabel}
          odds={odds}
          side={side}
        >
          {/* Not Pretty but works */}
          <slot>{this.renderVendorButton}</slot>
          {this.renderVendorDisclaimer}
          {isBranded && <sg-disclaimer slot="disclaimer" data-config={this.getVendorData}></sg-disclaimer>}
        </sportsline-pick-snippet-card>
      </Host>
    );
  }
}
